/**
 * Date/Time Utility Functions
 * Pure functions for date manipulation and formatting
 */

import { Timestamp } from 'firebase/firestore';

// Day names for formatting
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Convert a Firestore Timestamp or Date to a Date object
 */
export function toDate(value: Timestamp | Date | { toDate: () => Date } | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof (value as any).toDate === 'function') return (value as any).toDate();
  return null;
}

/**
 * Check if a date is today
 */
export function isToday(date: Date | null): boolean {
  if (!date) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

/**
 * Check if a date is in the future (after now)
 */
export function isFuture(date: Date | null): boolean {
  if (!date) return false;
  return date.getTime() > Date.now();
}

/**
 * Check if a date is in the past (before now)
 */
export function isPast(date: Date | null): boolean {
  if (!date) return false;
  return date.getTime() < Date.now();
}

/**
 * Check if a date is tomorrow
 */
export function isTomorrow(date: Date | null): boolean {
  if (!date) return false;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return (
    date.getFullYear() === tomorrow.getFullYear() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getDate() === tomorrow.getDate()
  );
}

/**
 * Check if a date is within the next N days
 */
export function isWithinDays(date: Date | null, days: number): boolean {
  if (!date) return false;
  const now = new Date();
  const futureDate = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  return date.getTime() >= now.getTime() && date.getTime() <= futureDate.getTime();
}

/**
 * Get the start of a day (midnight)
 */
export function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get the end of a day (23:59:59.999)
 */
export function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get the start of the current week (Sunday)
 */
export function startOfWeek(date: Date = new Date()): Date {
  const result = new Date(date);
  const day = result.getDay();
  result.setDate(result.getDate() - day);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get the end of the current week (Saturday 23:59:59)
 */
export function endOfWeek(date: Date = new Date()): Date {
  const result = new Date(date);
  const day = result.getDay();
  result.setDate(result.getDate() + (6 - day));
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get days until Sunday (0 = Sunday, so returns 0 if today is Sunday)
 */
export function getDaysUntilSunday(date: Date = new Date()): number {
  const dayOfWeek = date.getDay();
  return (7 - dayOfWeek) % 7;
}

/**
 * Format hour number to 12-hour string (e.g., 9 -> "9 AM", 14 -> "2 PM")
 */
export function formatHour(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}

/**
 * Format time from Date to 12-hour string (e.g., "9:30 AM")
 */
export function formatTime(date: Date | null): string {
  if (!date) return '';
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${ampm}`;
}

/**
 * Format date to readable string (e.g., "Mon, Mar 5")
 */
export function formatDateShort(date: Date | null): string {
  if (!date) return '';
  const dayName = DAY_NAMES_SHORT[date.getDay()];
  const monthName = MONTH_NAMES_SHORT[date.getMonth()];
  const dayOfMonth = date.getDate();
  return `${dayName}, ${monthName} ${dayOfMonth}`;
}

/**
 * Format date to full readable string (e.g., "Monday, March 5, 2026")
 */
export function formatDateFull(date: Date | null): string {
  if (!date) return '';
  const dayName = DAY_NAMES[date.getDay()];
  const monthName = MONTH_NAMES[date.getMonth()];
  const dayOfMonth = date.getDate();
  const year = date.getFullYear();
  return `${dayName}, ${monthName} ${dayOfMonth}, ${year}`;
}

/**
 * Format scheduled time (e.g., "Today at 9:30 AM", "Tomorrow at 2:00 PM", "Mon, Mar 5 at 9:00 AM")
 */
export function formatScheduledTime(date: Date | Timestamp | null): string {
  const d = date instanceof Date ? date : toDate(date);
  if (!d) return '';
  
  const time = formatTime(d);
  
  if (isToday(d)) {
    return `Today at ${time}`;
  }
  if (isTomorrow(d)) {
    return `Tomorrow at ${time}`;
  }
  return `${formatDateShort(d)} at ${time}`;
}

/**
 * Format relative time (e.g., "in 30 min", "2 hours ago", "just now")
 */
export function formatRelativeTime(date: Date | null): string {
  if (!date) return '';
  
  const now = Date.now();
  const diff = date.getTime() - now;
  const absDiff = Math.abs(diff);
  const isFutureTime = diff > 0;
  
  const minutes = Math.floor(absDiff / (1000 * 60));
  const hours = Math.floor(absDiff / (1000 * 60 * 60));
  const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
  
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return isFutureTime ? `in ${minutes} min` : `${minutes} min ago`;
  }
  if (hours < 24) {
    const unit = hours === 1 ? 'hour' : 'hours';
    return isFutureTime ? `in ${hours} ${unit}` : `${hours} ${unit} ago`;
  }
  if (days < 7) {
    const unit = days === 1 ? 'day' : 'days';
    return isFutureTime ? `in ${days} ${unit}` : `${days} ${unit} ago`;
  }
  
  // Fall back to formatted date for older/further dates
  return formatDateShort(date);
}

/**
 * Get minutes until a date
 */
export function getMinutesUntil(date: Date | null): number {
  if (!date) return 0;
  const diff = date.getTime() - Date.now();
  return Math.floor(diff / (1000 * 60));
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Add hours to a date
 */
export function addHours(date: Date, hours: number): Date {
  const result = new Date(date);
  result.setHours(result.getHours() + hours);
  return result;
}

/**
 * Add minutes to a date
 */
export function addMinutes(date: Date, minutes: number): Date {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() + minutes);
  return result;
}

/**
 * Check if two dates are on the same day
 */
export function isSameDay(date1: Date | null, date2: Date | null): boolean {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Get a date string for Firestore queries (YYYY-MM-DD)
 */
export function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a date string (YYYY-MM-DD) to Date
 */
export function parseDateString(dateStr: string): Date | null {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
}

/**
 * Format a duration in minutes to a human-readable string.
 * Examples:
 *   - 45 -> "45 min"
 *   - 60 -> "1h"
 *   - 75 -> "1h 15min"
 *   - 120 -> "2h"
 *   - 0 -> "0 min"
 * 
 * @param minutes - Duration in minutes (can be fractional, will be rounded)
 * @returns Formatted duration string
 */
export function formatDuration(minutes: number): string {
  const totalMinutes = Math.round(minutes);
  
  if (totalMinutes < 0) {
    return '0 min';
  }
  
  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }
  
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}min`;
}

/**
 * Calculate duration between two timestamps/dates in minutes.
 * Returns 0 if either value is null/undefined.
 */
export function getDurationMinutes(
  start: Timestamp | Date | { toDate: () => Date } | null | undefined,
  end: Timestamp | Date | { toDate: () => Date } | null | undefined
): number {
  const startDate = toDate(start);
  const endDate = toDate(end);
  
  if (!startDate || !endDate) {
    return 0;
  }
  
  const diffMs = endDate.getTime() - startDate.getTime();
  return Math.round(diffMs / (1000 * 60));
}

/**
 * Format a full datetime string (e.g., "3/26/2026, 11:30:00 AM")
 * Locale-aware, includes date and time with seconds.
 */
export function formatDateTime(date: Date | null): string {
  if (!date) return '';
  return date.toLocaleString();
}

/**
 * Format date and time without seconds (e.g., "Mon, Mar 26 at 11:30 AM")
 * Uses app-consistent formatting.
 */
export function formatDateTimeCompact(date: Date | null): string {
  if (!date) return '';
  return `${formatDateShort(date)} at ${formatTime(date)}`;
}

/**
 * Format for SMS/notification messages (e.g., "Mon, Mar 26, 11:30 AM")
 * Concise format suitable for notifications.
 */
export function formatDateTimeForNotification(date: Date | null): string {
  if (!date) return '';
  const dayName = DAY_NAMES_SHORT[date.getDay()];
  const monthName = MONTH_NAMES_SHORT[date.getMonth()];
  const dayOfMonth = date.getDate();
  const time = formatTime(date);
  return `${dayName}, ${monthName} ${dayOfMonth}, ${time}`;
}
